import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';

import CurrencyInput        from 'app/components/common/currency-input';
import EllipsisMenu         from 'app/components/common/ellipsis-menu';
import Icon                 from 'app/components/common/icon';
import MatchRatioSelector   from 'app/components/common/match-ratio-selector';
import Modal                from 'app/components/common/modal';
import CattrInput           from 'app/components/company-admin/common/cattr-input';
import BudgetsDuck          from 'app/ducks/company-admin/budgets';
import CadminSlx            from 'app/selectors/company-admin/';
import PageMatchSettingsSlx from 'app/selectors/company-admin/page-settings';
import FfSlx                from 'app/selectors/feature-flags';

const emptyTier = {condition: null, matchPercent: 0, employeeAmount: null};

class ModalMatchTiers extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      tiers: [],
      defaultTier: {...emptyTier},
      showValidation: false,
    };

    this.onClose = this.onClose.bind(this);
    this.onClickSave = this.onClickSave.bind(this);
    this.onChangePercent = this.onChangePercent.bind(this);
    this.onChangeEmpCap = this.onChangeEmpCap.bind(this);
    this.onChangeCondition = this.onChangeCondition.bind(this);
    this.onClickNewTier = this.onClickNewTier.bind(this);
  }

  componentDidMount() {
    const {budget} = this.props;
    this.setState({
      tiers: budget.tiers,
      defaultTier: {matchPercent: budget.matchPercent, employeeAmount: budget.employeeAmount},
    });
  }

  getTierValidations(tier) {
    const obj = {};
    if (!Number.isFinite(tier?.matchPercent)) {
      obj.matchPercent = ['required'];
    }
    if (!Number.isFinite(tier?.employeeAmount)) {
      obj.employeeAmount = ['required'];
    }
    if (!tier?.condition) {
      obj.condition = ['required'];
    }
    return Object.keys(obj).length ? obj : null;
  }

  get allowNewTier() {
    return this.props.ffCattrs || this.hasTiers;
  }

  get hasTiers() {
    return !!this.state.tiers.length;
  }

  get saveAttrs() {
    const {tiers, defaultTier} = this.state;
    return {
      // tiers,
      tiers: tiers.filter(t => !this.getTierValidations(t)),
      matchPercent: defaultTier.matchPercent,
      employeeAmount: defaultTier.employeeAmount,
    };
  }

  get isValid() {
    const {tiers} = this.state;
    return !tiers.find(t => this.getTierValidations(t));
  }

  onClose() {
    this.props.onClose(false);
  }

  async onClickSave() {
    if (this.isValid) {
      await this.props.save(this.saveAttrs);
      this.props.onClose(true);
    } else {
      this.setState({showValidation: true});
    }
  }

  onClickNewTier() {
    this.setState((prevState) => {
      return {...prevState, tiers: [...prevState.tiers, {...emptyTier}]};
    });
  }

  moveTier(prevIndex, newIndex) {
    this.setState((prevState) => {
      const newTiers = [...prevState.tiers];
      const [tier] = newTiers.splice(prevIndex, 1);
      newTiers.splice(newIndex, 0, tier);
      return {tiers: newTiers};
    });
  }
  onClickMoveUp(index) {
    this.moveTier(index, index-1);
  }
  onClickMoveDown(index) {
    this.moveTier(index, index+1);
  }

  onClickDeleteTier(index) {
    this.setState((prevState) => {
      const newTiers = [...prevState.tiers];
      newTiers.splice(index, 1);
      return {tiers: newTiers};
    });
  }

  onChangeCondition(index, {option}) {
    const cattrOptionId = option?.id || null;
    this.setState((prevState) => {
      const newTiers = [...prevState.tiers];
      const newTier = {...newTiers[index], condition: cattrOptionId};
      newTiers[index] = newTier;
      return {...prevState, tiers: newTiers};
    });
  }

  onChangePercent(index, matchPercent) {
    const isDefault = index === -1;
    this.setState((prevState) => {
      if (isDefault) {
        return {...prevState, defaultTier: {...prevState.defaultTier, matchPercent}};
      }
      const newTiers = [...prevState.tiers];
      const newTier = {...newTiers[index], matchPercent};
      newTiers[index] = newTier;
      return {...prevState, tiers: newTiers};
    });
  }

  onChangeEmpCap(index, {amount: employeeAmount}) {
    const isDefault = index === -1;
    this.setState((prevState) => {
      if (isDefault) {
        return {...prevState, defaultTier: {...prevState.defaultTier, employeeAmount}};
      }
      const newTiers = [...prevState.tiers];
      const newTier = {...newTiers[index], employeeAmount};
      newTiers[index] = newTier;
      return {...prevState, tiers: newTiers};
    });
  }

  renderTier(tier, index) {
    const isDefault = index === -1;
    const isFirst = index === 0;
    const isLast = index === this.state.tiers.length - 1;
    const validations = this.state.showValidation ? this.getTierValidations(tier) : null;
    return (
      <div className="ca-modal-tiers-tier" key={index}>
        <div className="ca-modal-tiers-tier-more">
          <label>&nbsp;</label>
          {!isDefault && (
            <div className="ca-modal-tiers-tier-input-con">
              <EllipsisMenu>
                <button disabled={isFirst} onClick={this.onClickMoveUp.bind(this, index)}><Icon.Caret direction="up" />Move Up</button>
                <button disabled={isLast} onClick={this.onClickMoveDown.bind(this, index)}><Icon.Caret direction="down" /> Move Down</button>
                <button onClick={this.onClickDeleteTier.bind(this, index)}><Icon.Bin /> Delete</button>
              </EllipsisMenu>
            </div>
          )}
        </div>
        <div className="ca-modal-tiers-tier-cond">
          {isDefault ? (<>
            <label className="right">&nbsp;</label>
            <div className="ca-modal-tiers-tier-input-con">All employees not in custom groups above</div>
          </>) : (<>
            <label>Condition</label>
            <CattrInput.Pair onChange={this.onChangeCondition.bind(this, index)} optionId={tier.condition} validations={validations} name="condition" />
          </>)}
        </div>
        <div className="ca-modal-tiers-tier-ratio">
          <label>Match Ratio</label>
          <MatchRatioSelector value={tier.matchPercent || 0} onChange={this.onChangePercent.bind(this, index)} validations={validations} name="matchPercent" />
        </div>
        <div className="ca-modal-tiers-tier-cap">
          <label>Annual Cap</label>
          <CurrencyInput amount={tier.employeeAmount} onChange={this.onChangeEmpCap.bind(this, index)} validations={validations} name="employeeAmount" />
        </div>
      </div>
    );
  }

  render() {
    const {tiers, defaultTier} = this.state;
    const {company, budget, isSaving} = this.props;
    const saveBtnDisabled = (() => {
      if (isSaving) return true;
      return false;
    })();

    return (
      <Modal onClose={this.onClose} className="bform ca-modal-tiers">
        <h1 className="bform-h1">Employee Match Settings</h1>
        <h2 className="bform-h2">Custom Match Groups</h2>
        <p className="ca-modal-tiers-p">Custom Match Groups let you assign different company match settings to employees based on Custom Attributes (like department or location). Employees must have a matching attribute to be included in a group.</p>
        <p className="ca-modal-tiers-p">If an employee qualifies for multiple groups, the first matching group listed will apply. If no match is found, the Default Match Group below will be used.</p>
        <p className="ca-modal-tiers-p">The Annual Cap refers to the maximum amount the company will contribute per employee per year.</p>
        {!this.allowNewTier && (
          <p className="ca-modal-tiers-p">If you'd like to use this feature, please reach out to your account manager.</p>
        )}
        {/* <pre>{JSON.stringify({saveAttrs: this.saveAttrs}, null, 3)}</pre> */}
        {tiers.map((tier, i) => this.renderTier(tier, i))}
        <button className="btn small secondary blue ca-modal-tiers-newbtn" onClick={this.onClickNewTier} disabled={!this.allowNewTier}>Add New Match Group</button>
        <h2 className="bform-h2">Default - All Other Employees</h2>
        {this.renderTier(defaultTier, -1)}
        <div className="bform-actions">
          <div />
          <button className="btn blue" onClick={this.onClickSave} disabled={saveBtnDisabled}>{isSaving ? 'Saving...' : 'Save'}</button>
        </div>
      </Modal>
    );
  }

}

ModalMatchTiers.propTypes = {
  onClose: PropTypes.func.isRequired,
};

const stateToProps = (state) => ({
  company: CadminSlx.company(state),
  budget: PageMatchSettingsSlx.budget(state),
  isSaving: BudgetsDuck.Slx.isSavingMatch(state),
  ffCattrs: FfSlx.cattrs(state),
});

const dispatchToProps = (dispatch) => ({
  save: (attrs) => dispatch(BudgetsDuck.Ax.updateMatch(attrs)),
});

export default connect(stateToProps, dispatchToProps)(ModalMatchTiers);
